import { FbEventType, FbStandardEvent } from '../../constants'
import { FacebookPixel } from '../../types'

export const fbPixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID
const testEventCode = import.meta.env.VITE_FACEBOOK_TEST_EVENT_CODE
const isDevelopment = import.meta.env.VITE_NODE_ENV === 'development'

// Helper function to validate pixel usage
const shouldBlockPixel = (): boolean => {
  // Block if in development mode AND using production pixel ID
  if (isDevelopment && !testEventCode) {
    console.warn(
      '[Facebook Pixel] Blocked: Cannot use production pixel ID in development environment. ' +
        'Use a test pixel ID in your local .env file for testing.'
    )

    return true
  }

  return false
}

export const fbInit = ({ email, name }: { email: string; name: string }) => {
  if (shouldBlockPixel()) return

  const [fn, ...rest] = name.split(' ')
  const ln = rest.join(' ')

  window.fbq?.(FbEventType.Init, fbPixelId, {
    em: email, // Email address
    fn: fn, // First Name
    ln: ln, // Last Name
  })
}

export const fbPageView = (parameters = {}) => {
  if (shouldBlockPixel()) return

  window.fbq?.(FbEventType.Track, FbStandardEvent.PageView, {
    ...parameters,
    test_event_code: testEventCode,
  })
}

// Fires a Meta STANDARD event (track), so Meta's algorithm can optimize toward
// real money signals (Lead/CompleteRegistration/Subscribe/Purchase) instead of
// downgraded custom events. See META_STANDARD_EVENT_MAP for the wiring.
export const fbStandardEvent = <E extends FbStandardEvent>(
  event: E,
  parameters?: FacebookPixel.EventParametersMap[E],
  options?: FacebookPixel.EventIDOptions
) => {
  if (shouldBlockPixel()) return

  window.fbq?.(
    FbEventType.Track,
    event,
    {
      ...parameters,
      test_event_code: testEventCode,
    } as FacebookPixel.EventParametersMap[E],
    options
  )
}

// https://developers.facebook.com/docs/facebook-pixel/advanced/
export const fbCustomEvent = (
  name: string,
  parameters = {},
  options?: FacebookPixel.EventIDOptions
) => {
  if (shouldBlockPixel()) return

  window.fbq?.(
    FbEventType.TrackCustom,
    name,
    {
      ...parameters,
      test_event_code: testEventCode,
    },
    options
  )
}
